<template>
  <modal :name="nomeModal" classes="v--modal cbm-modal" :adaptive="true" height="auto" :maxWidth="560" :clickToClose="!salvando" @closed="aoFecharModal">
    <div class="cbm-root">
      <h3 class="cbm-title cbm-title--primario"><fa-icon icon="envelope" /> Enviar extrato por e-mail</h3>
      <p class="cbm-desc">O cliente recebe o extrato de cashback em PDF, {{ descricaoPeriodo }}, conforme os filtros da tela.</p>

      <div class="cbm-cliente">
        <div class="cbm-cliente__icone"><fa-icon icon="user" /></div>
        <div class="cbm-cliente__dados">
          <div class="cbm-cliente__nome">{{ carteira.nome || `Cliente ${carteira.clienteId}` }}</div>
          <div class="cbm-cliente__codigo">Código {{ carteira.clienteId }}</div>
        </div>
      </div>

      <div v-if="sucesso" class="cbm-sucesso" role="status">
        <fa-icon icon="check-circle" class="cbm-aviso__icone" />
        <span>{{ sucesso }}</span>
      </div>

      <template v-else>
        <div class="cbm-campo">
          <span class="cbm-rotulo">E-mails do cliente</span>
          <div v-if="carregando" class="cbm-emails__estado">
            <fa-icon icon="spinner" spin /> Carregando os e-mails do cliente...
          </div>
          <div v-else-if="erroCarga" class="cbm-erro" role="alert">
            <fa-icon icon="exclamation-triangle" class="cbm-aviso__icone" />
            <div>
              Não foi possível carregar os e-mails: {{ erroCarga }}
              <button type="button" class="cbm-link" @click="carregarEmails">Tentar novamente</button>
            </div>
          </div>
          <div v-else-if="!emails.length" class="cbm-emails__estado">Nenhum e-mail cadastrado. Adicione um abaixo.</div>
          <ul v-else class="cbm-emails" aria-label="E-mails do cliente">
            <li v-for="item in emails" :key="item.email">
              <label class="cbm-emails__item">
                <input v-model="item.marcado" type="checkbox" :disabled="salvando" />
                <span>{{ item.email }}</span>
                <small v-if="item.adicionado" class="cbm-emails__etiqueta">adicionado</small>
              </label>
            </li>
          </ul>
        </div>

        <div class="cbm-campo">
          <label class="cbm-rotulo" :for="`${nomeModal}-novo`">Adicionar outro e-mail</label>
          <div class="cbm-emails__novo">
            <input
              :id="`${nomeModal}-novo`"
              v-model="novoEmail"
              type="email"
              :class="['cbm-input', { 'cbm-input--invalido': !!erroNovoEmail }]"
              placeholder="nome@empresa.com.br"
              autocomplete="off"
              :disabled="salvando"
              :aria-describedby="`${nomeModal}-novo-ajuda`"
              @keydown.enter.prevent="adicionarEmail"
            />
            <button type="button" class="cbm-btn cbm-btn-cancel" :disabled="salvando || !novoEmail.trim()" @click="adicionarEmail">
              <fa-icon icon="plus" /> Adicionar
            </button>
          </div>
          <div :id="`${nomeModal}-novo-ajuda`" class="cbm-ajuda">
            <span :class="{ 'cbm-ajuda--erro': !!erroNovoEmail || excedeuLimite }">
              {{ erroNovoEmail || (excedeuLimite ? `Selecione no máximo ${LIMITE_EMAILS} e-mails.` : `Até ${LIMITE_EMAILS} destinatários.`) }}
            </span>
            <span :class="{ 'cbm-contador--ok': selecionados.length > 0 && !excedeuLimite }">{{ selecionados.length }} selecionado{{ selecionados.length === 1 ? "" : "s" }}</span>
          </div>
        </div>

        <div v-if="foraDoCadastro.length" class="cbm-aviso" role="status">
          <fa-icon icon="exclamation-triangle" class="cbm-aviso__icone" />
          <span>
            {{ foraDoCadastro.join(", ") }} {{ foraDoCadastro.length === 1 ? "não está" : "não estão" }} no cadastro do cliente. Confira antes de enviar: o
            extrato vai para quem receber o e-mail.
          </span>
        </div>

        <div v-if="erro" class="cbm-erro" role="alert">
          <fa-icon icon="exclamation-triangle" class="cbm-aviso__icone" />
          <span>{{ erro }}</span>
        </div>
      </template>

      <div class="cbm-footer">
        <button type="button" class="cbm-btn cbm-btn-cancel" :disabled="salvando" @click="fechar">{{ sucesso ? "Fechar" : "Cancelar" }}</button>
        <button v-if="!sucesso" type="button" class="cbm-btn cbm-btn-primario" :disabled="salvando || !podeEnviar" @click="enviar">
          <fa-icon :icon="salvando ? 'spinner' : 'envelope'" :spin="salvando" /> {{ salvando ? "Enviando..." : "Enviar" }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { get, post } from "@/helpers/apiRequest";
import { descricaoPeriodoExtrato, mensagemErroApi } from "../helpers";
import modalVisivel from "./modalVisivel";

export const LIMITE_EMAILS = 10;
const FORMATO_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const emailValido = (valor) => FORMATO_EMAIL.test(String(valor || "").trim());

export function emailsDoCliente(lista, adicionados = []) {
  const vistos = new Set(adicionados.map((item) => item.email.toLowerCase()));
  let marcados = adicionados.filter((item) => item.marcado).length;

  return lista.reduce((emails, valor) => {
    const email = String(valor || "").trim().toLowerCase();

    if (!email || vistos.has(email)) return emails;

    vistos.add(email);

    const marcado = emailValido(email) && marcados < LIMITE_EMAILS;

    if (marcado) marcados += 1;

    emails.push({ email, marcado, adicionado: false });

    return emails;
  }, []);
}

export default {
  name: "EnviarExtratoEmailModal",
  mixins: [modalVisivel],

  props: {
    carteira: { type: Object, required: true },
    periodo: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      LIMITE_EMAILS,
      emails: [],
      novoEmail: "",
      erroNovoEmail: null,
      carregando: false,
      erroCarga: null,
      salvando: false,
      erro: null,
      sucesso: null,
    };
  },

  computed: {
    urlBase() {
      return `/admin/cashback/clientes/${encodeURIComponent(this.carteira.clienteId)}/extrato`;
    },

    selecionados() {
      return this.emails.filter((item) => item.marcado).map((item) => item.email);
    },

    foraDoCadastro() {
      return this.emails.filter((item) => item.marcado && item.adicionado).map((item) => item.email);
    },

    excedeuLimite() {
      return this.selecionados.length > LIMITE_EMAILS;
    },

    podeEnviar() {
      return !this.carregando && this.selecionados.length > 0 && !this.excedeuLimite;
    },

    descricaoPeriodo() {
      return descricaoPeriodoExtrato(this.periodo || {});
    },
  },

  methods: {
    aoAbrir() {
      Object.assign(this, { emails: [], novoEmail: "", erroNovoEmail: null, erroCarga: null, erro: null, sucesso: null, salvando: false });
      this.carregarEmails();
    },

    carregarEmails() {
      this.carregando = true;
      this.erroCarga = null;

      return get(`${this.urlBase}/emails`)
        .then((json) => {
          const adicionados = this.emails.filter((item) => item.adicionado);

          this.emails = [...emailsDoCliente((json && json.emails) || [], adicionados), ...adicionados];
        })
        .catch((err) => {
          this.erroCarga = mensagemErroApi(err);
        })
        .finally(() => {
          this.carregando = false;
        });
    },

    adicionarEmail() {
      const email = this.novoEmail.trim().toLowerCase();

      if (!email) return;

      if (!emailValido(email)) {
        this.erroNovoEmail = "Informe um e-mail válido.";

        return;
      }

      const existente = this.emails.find((item) => item.email.toLowerCase() === email);

      if (existente) {
        existente.marcado = true;
      } else {
        this.emails.push({ email, marcado: true, adicionado: true });
      }

      this.novoEmail = "";
      this.erroNovoEmail = null;
    },

    payload() {
      const { dtIni, dtFim, tipos } = this.periodo || {};
      const corpo = { emails: this.selecionados };

      if (dtIni) corpo.dtIni = dtIni;
      if (dtFim) corpo.dtFim = dtFim;
      if (Array.isArray(tipos) && tipos.length) corpo.tipos = tipos;

      return corpo;
    },

    enviar() {
      if (this.salvando || !this.podeEnviar) return Promise.resolve();

      this.salvando = true;
      this.erro = null;

      return post(`${this.urlBase}/email`, this.payload())
        .then((json) => {
          this.sucesso = (json && json.message) || "Extrato enviado por e-mail.";
          this.$emit("enviado", json);
        })
        .catch((err) => {
          this.erro = mensagemErroApi(err);
        })
        .finally(() => {
          this.salvando = false;
        });
    },
  },
};
</script>

<style scoped src="./cashbackModal.css"></style>
<style src="./cashbackModalCaixa.css"></style>

<style scoped>
.cbm-title--primario svg {
  color: var(--cashback-cor-primaria, #3a3e81);
}

.cbm-btn-primario {
  background: var(--cashback-cor-primaria, #3a3e81);
  color: #fff;
}

.cbm-btn-primario:hover:not(:disabled) {
  background: var(--cashback-cor-primaria, #3a3e81);
  filter: brightness(0.85);
  box-shadow: 0 4px 12px rgba(var(--cashback-cor-primaria-rgb, 58, 62, 129), 0.25);
}

.cbm-emails {
  max-height: 220px;
  margin: 0;
  padding: 4px;
  overflow-y: auto;
  list-style: none;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
}

.cbm-emails__item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #1f2937;
  cursor: pointer;
}

.cbm-emails__item:hover {
  background: #f9fafb;
}

.cbm-emails__item span {
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

.cbm-emails__etiqueta {
  margin-left: auto;
  padding: 1px 8px;
  border-radius: 10px;
  background: #eef2ff;
  font-size: 0.7rem;
  color: var(--cashback-cor-primaria, #3a3e81);
}

.cbm-emails__estado {
  padding: 12px 14px;
  border: 1.5px dashed #e5e7eb;
  border-radius: 10px;
  font-size: 0.8rem;
  color: #6b7280;
}

.cbm-emails__novo {
  display: flex;
  gap: 8px;
}

.cbm-emails__novo .cbm-btn {
  flex-shrink: 0;
}

.cbm-link {
  margin-left: 6px;
  padding: 0;
  border: none;
  background: none;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.cbm-sucesso {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #ecfdf3;
  font-size: 0.82rem;
  color: #027a48;
}
</style>
